import {
  Evaluator,
  parseTemplate,
  ScriptError,
  type History,
  type PlannedExercise,
  type TemplateNode,
  type WorkoutNode,
  type WorkoutPlan,
} from '@gymcloud/mixedscript'

import type { Measurements } from '~/data/types'

/**
 * A workout template is MixedScript, kept as text — the script is the artifact. What it means
 * is `@gymcloud/mixedscript`; this file is only how it travels and how a plan becomes a draft.
 */
export interface WorkoutTemplate {
  readonly id: string
  /** Read out of the script when it is saved, so a list costs no parsing. */
  readonly name: string
  readonly script: string
  /** The version it was copied out of Explore from, when it was. */
  readonly forkedFromVersionId?: string | null
  /** What Explore offers for it, or null while it is nobody's but yours. */
  readonly publishedVersionId?: string | null
  /** False when the script has been edited since it was published. */
  readonly publishedIsCurrent?: boolean
  readonly createdAt: number
  /** When it was last written, as an ISO-8601 string with milliseconds. */
  readonly updatedAt: string
}

/**
 * A template's script as it stood at one moment, and never changed after. A run follows one of
 * these rather than a template, so nothing anybody is in the middle of moves under them.
 */
export interface TemplateVersion {
  readonly id: string
  /** Null once the template it came out of is gone. The version, and the runs of it, stay. */
  readonly templateId: string | null
  readonly version: number
  readonly name: string
  readonly script: string
  readonly createdAt: number
}

/** Starting a template. The one with no end is the active template. */
export interface Enrollment {
  readonly id: string
  /**
   * The version being followed. Empty on a run started on this device and not yet sent: the
   * server is what mints a version, and the run is pointed at one the moment it lands.
   */
  readonly versionId: string
  readonly templateId: string | null
  /** Frozen when the run started, so the history keeps the name it ran under. */
  readonly templateName: string
  readonly authorName?: string | null
  /** Whether the template it follows is one of yours. */
  readonly mine?: boolean
  /** What Explore offers now, so a run can say a newer version is there. */
  readonly listedVersionId?: string | null
  readonly startedAt: number
  readonly endedAt: number | null
  /**
   * Which weekday this run's strict cycle begins on, 1 for Monday through 7 for Sunday. Null on
   * a schedule with no cycle to land, and on runs older than the question being asked — those
   * keep counting from the day they began.
   */
  readonly anchorDay?: number | null
  /**
   * How many workouts were finished under this run. Counted by the server: a client holds the
   * most recent handful of workouts and would answer with whatever happened to be loaded.
   */
  readonly workoutCount?: number
}

/**
 * Which template a run has a page to open, given the library it is read beside. A run of one of
 * your own opens yours; a run of somebody's listing opens the listing. A run of a template that
 * has been deleted and unpublished opens nothing — there is no page left for it, and offering
 * one would only lead to a screen saying so.
 *
 * A run keeps its `templateId` after the template is unpublished, so the id alone is not the
 * question: what is asked is whether anybody can still reach what it names.
 */
export function openableTemplateId(
  enrollment: Enrollment,
  library: readonly WorkoutTemplate[],
): string | null {
  const id = enrollment.templateId
  if (id === null) {
    return null
  }
  return library.some((held) => held.id === id) || enrollment.listedVersionId != null ? id : null
}

/** How many times one workout of a template has been done, across every run of it. */
export interface WorkoutsDone {
  readonly name: string
  readonly count: number
}

/** A template's place in Explore, as a row of it reads. */
export interface LibraryListing {
  readonly templateId: string
  readonly versionId: string
  readonly name: string
  readonly description: string | null
  readonly authorName: string | null
  readonly mine: boolean
  readonly schedule: string
  readonly workoutCount: number
  readonly cycleDays: number | null
  /** The exercises the template names, in the order its script names them. */
  readonly exercises: readonly string[]
  readonly publishedAt: number
  /** People who have ever run it, and people running it now. */
  readonly ranCount: number
  readonly runningCount: number
}

/** A listing with everything its own page shows. */
export interface LibraryTemplate extends LibraryListing {
  readonly script: string
  readonly running: boolean
}

/** How Explore is narrowed. Everything is optional; nothing set is everything published. */
export interface ExploreQuery {
  readonly search?: string | undefined
  readonly schedule?: string | undefined
  readonly workouts?: number | undefined
  readonly exercise?: string | undefined
  readonly sort?: 'popular' | 'recent' | undefined
}

export const isActive = (enrollment: Enrollment): boolean => enrollment.endedAt === null

export interface TargetJson {
  readonly repsLow?: number | null
  readonly repsHigh?: number | null
  readonly repsOpen?: boolean
  readonly weightKg?: number | null
  readonly secondsLow?: number | null
  readonly secondsHigh?: number | null
  readonly metersLow?: number | null
  readonly metersHigh?: number | null
}

export interface ContextSet {
  readonly exercise: string
  readonly warmup?: boolean
  readonly logged?: boolean
  readonly target?: TargetJson | null
  readonly reps?: number | null
  readonly weightKg?: number | null
  readonly durationSeconds?: number | null
  readonly distanceMeters?: number | null
  readonly rpe?: number | null
}

export interface ContextSession {
  readonly workoutId?: string
  readonly workout?: string | null
  readonly startedAt?: number
  readonly sets: ContextSet[]
}

/**
 * Everything evaluating a template is allowed to look at. It comes from the server rather than
 * from whatever the browser has paged in, because the same script and the same history have to
 * give the same plan everywhere.
 */
export interface TemplateContext {
  readonly sessions: ContextSession[]
  readonly history: Record<string, ContextSet[]>
}

/** One set a template planned, as it is written down against the workout it was planned for. */
export interface PlanEntry {
  readonly exerciseName: string
  readonly isWarmup: boolean
  readonly target: TargetJson | null
  /** Which of the workout's logged sets this was, while the ids are still being handed out. */
  readonly loggedIndex?: number | null
  /** The set it became, once there is one. Null means it was passed over. */
  readonly setId?: string | null
  /**
   * A set the assistant asked for rather than the template: kept with what it asked, and no part
   * of any session — /specs/features/assistant/workout.spec.md.
   */
  readonly byAssistant?: boolean
}

/** The active template, ready to plan from: its script, its history, and what it offers next. */
export interface ActiveTemplate {
  readonly enrollment: Enrollment
  /** The template in your library, when the run is of one of yours. */
  readonly template: WorkoutTemplate | null
  /** What it is called, off the version the run follows. */
  readonly name: string
  readonly script: string
  readonly node: TemplateNode | null
  readonly history: History
  readonly next: WorkoutNode | null
  /** What is wrong with the script, when it will not parse. */
  readonly error: string | null
}

const value = (held: number | null | undefined): number | undefined => held ?? undefined

function toHistSet(set: ContextSet) {
  return {
    exercise: set.exercise,
    reps: value(set.reps),
    weightKg: value(set.weightKg),
    seconds: value(set.durationSeconds),
    meters: value(set.distanceMeters),
    rpe: value(set.rpe),
    warmup: set.warmup ?? false,
    logged: set.logged ?? false,
    target: set.target
      ? {
          repsLow: value(set.target.repsLow),
          repsHigh: value(set.target.repsHigh),
          repsOpen: set.target.repsOpen ?? false,
          weightKg: value(set.target.weightKg),
          secondsLow: value(set.target.secondsLow),
          secondsHigh: value(set.target.secondsHigh),
          metersLow: value(set.target.metersLow),
          metersHigh: value(set.target.metersHigh),
        }
      : undefined,
  }
}

export function toHistory(context: TemplateContext): History {
  return {
    sessions: context.sessions.map((session) => ({
      workout: session.workout ?? '',
      sets: session.sets.map(toHistSet),
    })),
    allSets: Object.fromEntries(
      Object.entries(context.history).map(([name, sets]) => [name, sets.map(toHistSet)]),
    ),
  }
}

/** What a planned exercise records, worked out from what its sets ask for. */
export function plannedMeasurements(exercise: PlannedExercise): Measurements {
  return {
    weight: exercise.sets.some((set) => set.target.weightKg !== undefined),
    reps: exercise.sets.some((set) => set.target.repsLow !== undefined),
    duration: exercise.sets.some((set) => set.target.secondsLow !== undefined),
    distance: exercise.sets.some((set) => set.target.metersLow !== undefined),
  }
}

/** The name a script gives itself, without evaluating anything else about it. */
export function scriptName(script: string): string {
  try {
    return parseTemplate(script).name
  } catch {
    return 'Untitled'
  }
}

/** The script a new template starts from. */
export function blankScript(name: string): string {
  return [
    'mixedscript 1',
    '',
    `template "${name}" (schedule: sequential) {`,
    '  workout "Day 1" {',
    '    exercise "Squat" (snap: 2.5kg, rest: 3m) {',
    '      3 x 5 @ 60kg',
    '    }',
    '  }',
    '}',
    '',
  ].join('\n')
}

/** What is wrong with a script, or null when nothing is. */
export function scriptProblem(script: string): string | null {
  try {
    parseTemplate(script)
    return null
  } catch (error) {
    return error instanceof ScriptError ? error.located : String(error)
  }
}

/** One entry per thing performed: alternatives as one entry, a superset's members together. */
export function planDetail(plan: WorkoutPlan): string {
  const parts: string[] = []
  let index = 0
  const exercises = plan.exercises
  while (index < exercises.length) {
    const exercise = exercises[index]
    if (exercise === undefined) {
      break
    }
    const group = exercise.superset
    if (group !== undefined) {
      const members: string[] = []
      let held = exercises[index]
      while (held?.superset === group) {
        members.push(held.name)
        index++
        held = exercises[index]
      }
      parts.push(members.join(' + '))
    } else {
      parts.push(
        exercise.alternatives.length > 1
          ? exercise.alternatives.map((alternative) => alternative.name).join(' / ')
          : exercise.name,
      )
      index++
    }
  }
  return parts.join(', ')
}

export { Evaluator, parseTemplate }
